export type ChunkErrorSource = "vite:preloadError" | "router.onError" | "captureException";

export type ChunkErrorInfo = {
	source: ChunkErrorSource;
	/** False when Vite reported a dynamic-import failure whose message is not a known chunk-load message. */
	chunkError: boolean;
	message: string;
	chunkUrl?: string;
	targetRoute?: string;
	targetPath?: string;
	fromPath?: string;
	pageUrl?: string;
};

export type ChunkErrorReporter = (error: unknown, info: ChunkErrorInfo) => void;

type RouteLike = { name?: unknown; path?: string; fullPath?: string };

type RouterLike = {
	onError(handler: (error: unknown, to: RouteLike, from: RouteLike) => void): unknown;
};

const CHUNK_ERROR_PATTERNS = [
	// Chrome, Edge
	/Failed to fetch dynamically imported module/i,
	// Firefox
	/error loading dynamically imported module/i,
	// Safari
	/Importing a module script failed/i,
	// Missing chunk answered with index.html (text/html) instead of JavaScript
	/Failed to load module script/i,
	/not a valid JavaScript MIME type/i,
	/disallowed MIME type/i,
	// Vite, when a CSS dependency of a lazy chunk fails
	/Unable to preload CSS/i,
];

export function errorMessage(error: unknown): string {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	if (error && typeof (error as { message?: unknown }).message === "string") return (error as { message: string }).message;
	return String(error);
}

export function isChunkLoadError(error: unknown): boolean {
	const message = errorMessage(error);
	return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function extractChunkUrl(message: string): string | undefined {
	const absolute = message.match(/https?:\/\/[^\s"'“”‘’()<>]+/);
	if (absolute) return trimUrl(absolute[0]);
	const asset = message.match(/\/assets\/[^\s"'“”‘’()<>]+/);
	return asset ? trimUrl(asset[0]) : undefined;
}

function trimUrl(url: string): string {
	return url.replace(/[.,;:]+$/, "");
}

const reported = new WeakSet<object>();

/** True the first time an error object is seen, false after. Primitives are never deduplicated. */
export function claimForReport(error: unknown): boolean {
	if (error === null || (typeof error !== "object" && typeof error !== "function")) return true;
	if (reported.has(error)) return false;
	reported.add(error);
	return true;
}

export function describeChunkError(error: unknown, info: Pick<ChunkErrorInfo, "source"> & Partial<ChunkErrorInfo>): ChunkErrorInfo {
	const message = errorMessage(error);
	return {
		...info,
		chunkError: isChunkLoadError(error),
		message,
		chunkUrl: info.chunkUrl ?? extractChunkUrl(message),
	};
}

function routeName(route: RouteLike | undefined): string | undefined {
	return typeof route?.name === "string" ? route.name : undefined;
}

type InstallOptions = {
	target: Pick<EventTarget, "addEventListener" | "removeEventListener">;
	router: RouterLike;
	report: ChunkErrorReporter;
	pageUrl?: () => string;
	logError?: (error: unknown) => void;
	/**
	 * Called synchronously for every chunk-load failure seen, whether or not it was already reported, so
	 * the UI can tell the user. It must be idempotent. Reporting to Sentry is separate and deduplicated.
	 */
	onFailure?: (error: unknown) => void;
};

/**
 * Logging only: the page is not reloaded, the event is not cancelled, and the router keeps rejecting
 * the navigation. `router.onError` replaces vue-router's own `console.error` for navigation errors,
 * so it is logged here to keep that output.
 */
export function installChunkErrorReporting({ target, router, report, pageUrl, logError = (e) => console.error(e), onFailure }: InstallOptions): () => void {
	const send = (error: unknown, info: Pick<ChunkErrorInfo, "source"> & Partial<ChunkErrorInfo>) => {
		try {
			if (!claimForReport(error)) return;
			report(error, describeChunkError(error, { pageUrl: pageUrl?.(), ...info }));
		} catch {
			// Reporting must never break navigation or the preload path.
		}
	};

	const notify = (error: unknown) => {
		try {
			onFailure?.(error);
		} catch {
			// A broken failure UI must not break navigation either.
		}
	};

	// Vite rethrows the error right after this event and vue-router passes it to router.onError within the
	// same microtask chain. Reporting on a timer lets onError claim it first, so the report carries the
	// target route. A preload failure outside a navigation (a plain import() call) is still reported.
	const onPreloadError = (event: Event) => {
		const payload = (event as Event & { payload?: unknown }).payload;
		// Vite also dispatches this for an error thrown while evaluating a loaded chunk, which is not a load failure.
		if (isChunkLoadError(payload)) notify(payload);
		setTimeout(() => send(payload, { source: "vite:preloadError" }), 0);
	};
	target.addEventListener("vite:preloadError", onPreloadError);

	router.onError((error, to, from) => {
		logError(error);
		if (!isChunkLoadError(error)) return;
		notify(error);
		send(error, {
			source: "router.onError",
			targetRoute: routeName(to),
			targetPath: to?.path,
			fromPath: from?.path,
		});
	});

	// A bare import() that nobody catches. Sentry's global handler reports it, so this only tells the user.
	const onUnhandledRejection = (event: Event) => {
		const reason = (event as Event & { reason?: unknown }).reason;
		if (isChunkLoadError(reason)) notify(reason);
	};
	target.addEventListener("unhandledrejection", onUnhandledRejection);

	return () => {
		target.removeEventListener("vite:preloadError", onPreloadError);
		target.removeEventListener("unhandledrejection", onUnhandledRejection);
	};
}
